// Pro-aktiv token-refresh med backoff + clear-on-failure.
//
// Supabase auto-refreshar tokens internt, men long-lived tabs på
// flaky network kan missa refresh-cyclen och hamna med expired
// access-token tills nästa page-load. Denna helper wraps
// `supabase.auth.refreshSession()` i 3 retries med exponentiell
// backoff (200ms, 800ms, 3200ms); om alla tre failar returneras
// 'failed' så callern (AuthContext) kan signa ut användaren och
// trigga en clean login-flow.

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export type RefreshResult = 'ok' | 'failed';

const MAX_ATTEMPTS = 3;
const BASE_DELAY_MS = 200;

/** Test-only overrides. Not part of public surface. */
export const _testHooks = {
  sleepOverride: null as null | ((ms: number) => Promise<void>),
  refreshOverride: null as null | (() => Promise<{ error: { message: string } | null }>),
};

function sleep(ms: number): Promise<void> {
  if (_testHooks.sleepOverride) return _testHooks.sleepOverride(ms);
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function callRefresh(): Promise<{ error: { message: string } | null }> {
  if (_testHooks.refreshOverride) return _testHooks.refreshOverride();
  const { error } = await supabase.auth.refreshSession();
  return { error: error ? { message: error.message } : null };
}

/**
 * Single-flight guard: if multiple callers invoke `refreshSessionWithBackoff`
 * concurrently, they all await the same underlying refresh. Without this,
 * parallel refresh calls race against Supabase's refresh-token rotation
 * and the losers get `refresh_token_not_found`, silently logging the user
 * out a few seconds after sign-in.
 */
let inflight: Promise<RefreshResult> | null = null;

/**
 * Refresh the current session with up to `MAX_ATTEMPTS` retries and
 * exponential backoff. Returns `'ok'` on first success, `'failed'`
 * when all attempts exhausted. Concurrent calls share one underlying
 * refresh.
 */
export async function refreshSessionWithBackoff(): Promise<RefreshResult> {
  if (inflight) return inflight;
  inflight = (async (): Promise<RefreshResult> => {
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
      const { error } = await callRefresh();
      if (!error) return 'ok';
      logger.warn(`[auth] refresh attempt ${attempt + 1}/${MAX_ATTEMPTS} failed`, {
        message: error.message,
      });
      if (attempt < MAX_ATTEMPTS - 1) {
        await sleep(BASE_DELAY_MS * 4 ** attempt);
      }
    }
    return 'failed';
  })().finally(() => { inflight = null; });
  return inflight;
}
